import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FeaturePageShell } from "../features/_Shell";
import { cardBorderClassName } from "../../lib/cardStyles";
import { DEMO_BOOKING_URL } from "../../lib/pricing";
import { usePageTitle } from "../../lib/usePageTitle";

const FONT = { fontFamily: "Space Grotesk, sans-serif" };
const PANEL_BG = { background: "rgba(28, 28, 37, 0.7)" };

const CALL_TYPES = [
  {
    icon: "ri-file-list-3-line",
    title: "Quote requests",
    body: "Gets the caller's details and logs a follow-up so nothing sits in voicemail.",
  },
  {
    icon: "ri-shield-line",
    title: "Claims calls",
    body: "Takes down what happened and puts the caller through to you, or logs it for a callback.",
  },
  {
    icon: "ri-bill-line",
    title: "Billing questions",
    body: "Answers from what you've told it about your office, and logs anything it can't handle.",
  },
  {
    icon: "ri-id-card-line",
    title: "ID card requests",
    body: "Takes the request and creates a follow-up for your team.",
  },
  {
    icon: "ri-moon-line",
    title: "After-hours calls",
    body: "Answers nights and weekends with your hours and how to reach you.",
  },
  {
    icon: "ri-user-voice-line",
    title: "“Can I talk to my agent?”",
    body: "Screens the call and rings your cell, or connects directly. Your choice.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Tell it about your agency",
    body: "Your hours, address, the lines you write, and how you handle claims. Upload a document or type it in.",
  },
  {
    n: "02",
    title: "Choose who gets transfers",
    body: "Your cell or someone on your team. Screened or direct.",
  },
  {
    n: "03",
    title: "Review every call",
    body: "Transcripts and follow-ups land in your dashboard.",
  },
];

const FAQS = [
  {
    q: "Does it replace my staff?",
    a: "No. It answers when your team can't, and hands real conversations to people.",
  },
  {
    q: "Does it speak Spanish?",
    a: "Yes. It can answer your callers in Spanish as well as English.",
  },
  {
    q: "Can it quote or bind coverage?",
    a: "No. Quotes and coverage decisions stay with you and your licensed team. It gathers the details.",
  },
  {
    q: "Does it connect to my agency management system?",
    a: "Yes. We connect NeuroSphere to your agency management system when we set up your account.",
  },
  {
    q: "What does it cost?",
    a: "Plans start at $299 a month, month to month.",
    link: true,
  },
];

const PAGE_STYLE = `
  .ins-reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.2, 0.7, 0.2, 1);
  }
  .ins-reveal.is-visible { opacity: 1; transform: none; }

  .ins-card { transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease; }
  .ins-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px -16px rgba(138, 43, 226, 0.45); }

  .ins-step { opacity: 0; transform: translateX(-10px); transition: opacity 0.5s ease, transform 0.5s ease; }
  .ins-step.is-on { opacity: 1; transform: none; }

  .ins-ring { animation: ins-ring 1.2s ease-in-out infinite; }
  @keyframes ins-ring { 0%, 100% { transform: rotate(0); } 20% { transform: rotate(-14deg); } 40% { transform: rotate(14deg); } 60% { transform: rotate(-8deg); } 80% { transform: rotate(8deg); } }

  .ins-cue { animation: ins-bob 2s ease-in-out infinite; }
  @keyframes ins-bob { 0%, 100% { transform: translateY(0); opacity: 0.6; } 50% { transform: translateY(8px); opacity: 1; } }

  .ins-shimmer {
    background-size: 200% auto;
    animation: ins-shimmer 8s linear infinite;
  }
  @keyframes ins-shimmer { to { background-position: 200% center; } }

  @media (prefers-reduced-motion: reduce) {
    .ins-ring, .ins-cue, .ins-shimmer { animation: none !important; }
    .ins-reveal, .ins-step { opacity: 1 !important; transform: none !important; transition: none !important; }
    .ins-card:hover { transform: none; }
  }
`;

/** Adds is-visible to every .ins-reveal inside the page once it scrolls into view. */
function useScrollReveal(rootRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>(".ins-reveal"));
    if (typeof IntersectionObserver === "undefined") {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rootRef]);
}

function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      const pct = max > 0 ? el.scrollTop / max : 0;
      if (barRef.current) barRef.current.style.transform = `scaleX(${pct})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 h-[3px] z-50 pointer-events-none" aria-hidden="true">
      <div
        ref={barRef}
        className="h-full origin-left bg-gradient-to-r from-purple-500 to-orange-400"
        style={{ transform: "scaleX(0)" }}
      />
    </div>
  );
}

/** Example card that plays out like a live call when it scrolls into view. */
function MemoryDemoCard() {
  const ref = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setStep(4);
      return;
    }
    const timers: number[] = [];
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          [1, 2, 3, 4].forEach((s, i) => {
            timers.push(window.setTimeout(() => setStep(s), 300 + i * 900));
          });
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const on = (s: number) => `ins-step ${step >= s ? "is-on" : ""}`;

  return (
    <div ref={ref} className={`rounded-xl p-6 ${cardBorderClassName}`} style={PANEL_BG}>
      <p className="text-xs font-semibold uppercase tracking-wider text-orange-300 mb-4" style={FONT}>
        Example. Not a real caller.
      </p>

      <div className={`${on(1)} flex items-center gap-3 mb-4`}>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-purple-500/20">
          <i
            className={`ri-phone-line text-purple-300 ${step >= 1 && step < 2 ? "ins-ring" : ""}`}
            aria-hidden="true"
          />
        </span>
        <span className="text-sm text-gray-300" style={FONT}>
          {step < 2 ? "Incoming call…" : "Call answered"}
        </span>
      </div>

      <div className={on(2)}>
        <p className="flex items-center gap-2 text-white font-semibold mb-1" style={FONT}>
          <i className="ri-user-follow-line text-green-400" aria-hidden="true" />
          Returning caller recognized
        </p>
      </div>
      <div className={on(3)}>
        <p className="text-gray-300 text-sm leading-relaxed" style={FONT}>
          Wants to add a teen driver to her auto policy. Asked for a callback today.
        </p>
      </div>

      <div className={on(4)}>
        <div className="border-t border-white/10 my-4" />
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2" style={FONT}>
          Open item
        </p>
        <div className="flex items-center gap-2 text-white text-sm" style={FONT}>
          <i className="ri-checkbox-blank-circle-line text-purple-400" aria-hidden="true" />
          <span>Call back about adding a driver</span>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ question, answer, link }: { question: string; answer: string; link?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-6 py-4 text-left text-white font-semibold text-sm hover:bg-white/5 transition-colors"
        style={FONT}
      >
        <span>{question}</span>
        <span className="ml-4 text-purple-400 text-lg" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-4 text-sm text-gray-400 leading-relaxed" style={FONT}>
          {answer}
          {link && (
            <>
              {" "}
              <Link to="/pricing" className="text-purple-300 underline hover:text-purple-200">
                See pricing
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BookDemoButton() {
  return (
    <a
      href={DEMO_BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block px-8 py-4 rounded-lg bg-gradient-to-r from-purple-500 to-orange-400 hover:from-purple-600 hover:to-orange-500 transition-all duration-300 text-white font-semibold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 whitespace-nowrap cursor-pointer"
      style={FONT}
    >
      Book a demo
    </a>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="ins-reveal text-2xl md:text-3xl font-bold text-white mb-8 text-center" style={FONT}>
      {children}
    </h2>
  );
}

export default function InsurancePage() {
  usePageTitle("AI Receptionist for Insurance Agencies");
  const rootRef = useRef<HTMLDivElement>(null);
  useScrollReveal(rootRef);

  return (
    <FeaturePageShell>
      <style>{PAGE_STYLE}</style>
      <ScrollProgress />

      <div ref={rootRef} className="relative z-10">
        {/* Hero: fills the first screen below the header */}
        <section className="relative flex min-h-[calc(100svh-73px)] flex-col items-center justify-center px-6 pt-16 pb-28 text-center">
          <p
            className="ins-reveal text-sm font-semibold uppercase tracking-widest text-orange-300 mb-4"
            style={FONT}
          >
            For insurance agencies
          </p>
          <h1
            className="ins-reveal ins-shimmer text-4xl md:text-6xl font-extrabold bg-clip-text text-transparent mb-6 max-w-4xl"
            style={{
              fontFamily: "Orbitron, sans-serif",
              transitionDelay: "100ms",
              backgroundImage: "linear-gradient(90deg, #c084fc, #f472b6, #fb923c, #f472b6, #c084fc)",
            }}
          >
            Every call answered. Even when you're with a client.
          </h1>
          <p
            className="ins-reveal text-lg md:text-xl text-gray-300 max-w-3xl tracking-wide mb-10"
            style={{ ...FONT, transitionDelay: "200ms" }}
          >
            NeuroSphere gives your agency an AI receptionist that answers your line, knows your office, remembers
            returning callers, and puts the important calls through to you.
          </p>
          <div
            className="ins-reveal flex flex-col sm:flex-row items-center gap-4"
            style={{ transitionDelay: "300ms" }}
          >
            <BookDemoButton />
            <Link
              to="/pricing"
              className="px-8 py-4 rounded-lg border border-white/20 bg-white/5 backdrop-blur-sm text-white/90 hover:border-white/40 hover:text-white transition-colors whitespace-nowrap"
              style={FONT}
            >
              See pricing
            </Link>
          </div>

          <a
            href="#calls"
            className="ins-cue absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-xs text-gray-400 hover:text-white"
            style={FONT}
          >
            <span>See how it works</span>
            <i className="ri-arrow-down-s-line text-2xl" aria-hidden="true" />
          </a>
        </section>

        {/* Call types */}
        <section id="calls" className="max-w-6xl mx-auto px-6 pt-12 pb-24 scroll-mt-20">
          <SectionHeading>The calls your office gets every day</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CALL_TYPES.map((c, i) => (
              <div
                key={c.title}
                className={`ins-reveal ins-card rounded-xl p-6 backdrop-blur-sm ${cardBorderClassName}`}
                style={{ ...PANEL_BG, transitionDelay: `${(i % 3) * 90}ms` }}
              >
                <i className={`${c.icon} text-3xl text-purple-400 mb-4 block`} aria-hidden="true" />
                <h3 className="text-lg font-semibold text-white mb-2" style={FONT}>
                  {c.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm" style={FONT}>
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Caller memory */}
        <section className="max-w-6xl mx-auto px-6 pb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="ins-reveal">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={FONT}>
                It remembers your callers
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed" style={FONT}>
                When a client calls back, your receptionist knows who they are and what they called about last time.
                No more {"“"}can I get your name and policy number again.{"”"}
              </p>
            </div>
            <div className="ins-reveal backdrop-blur-sm" style={{ transitionDelay: "150ms" }}>
              <MemoryDemoCard />
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="max-w-6xl mx-auto px-6 pb-24">
          <SectionHeading>Live in three steps</SectionHeading>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
            <div
              className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-orange-400/0"
              aria-hidden="true"
            />
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className={`ins-reveal ins-card relative rounded-xl p-6 backdrop-blur-sm ${cardBorderClassName}`}
                style={{ ...PANEL_BG, transitionDelay: `${i * 150}ms` }}
              >
                <div
                  className="text-3xl font-extrabold bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent mb-3"
                  style={{ fontFamily: "Orbitron, sans-serif" }}
                >
                  {s.n}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={FONT}>
                  {s.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm" style={FONT}>
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-6 pb-24">
          <SectionHeading>Good to know</SectionHeading>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <div key={f.q} className="ins-reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <FaqItem question={f.q} answer={f.a} link={f.link} />
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="ins-reveal flex flex-col items-center pb-28 px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={FONT}>
            Hear it answer for your agency
          </h2>
          <p className="text-gray-300 max-w-2xl mb-8" style={FONT}>
            We'll set up a demo receptionist with your agency's name and details, so you can call it yourself.
          </p>
          <BookDemoButton />
        </section>
      </div>
    </FeaturePageShell>
  );
}
